/**
 * 配送希望日・配送希望時間の入力をカート属性として保存する。
 *
 * カートのフォームを送信すれば属性は保存されるが、それだけだと
 * 数量変更でカートが再描画されたときや、アクセラレーテッドチェックアウトボタン
 * （Shop Pay など）から直接進んだときに入力が失われる。
 * そのため変更のたびに cart/update.js へ保存しておく。
 *
 * 日付は自作のカレンダーから選ばせる。ブラウザ標準の日付入力は
 * 見た目を揃えられないうえ、手入力できてしまい誤記入の原因になるため、
 * 値の保管先としてだけ残して非表示にする。
 * JS が動かなかった場合は標準の日付入力がそのまま残るので、入力不能にはならない。
 */

const ATTRIBUTE_NAME_PATTERN = /^attributes\[(.+)\]$/;

const HTML_ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

const KEY_DAY_OFFSETS = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

/**
 * ローカルタイムの Date を YYYY-MM-DD に変換する。
 * toISOString() は UTC に変換してしまい、日本時間では日付が1日ずれるため使わない。
 */
function toIsoDate(date) {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

function fromIsoDate(value) {
  const [year, month, day] = value.split('-').map(Number);

  return new Date(year, month - 1, day);
}

function addDays(date, count) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);
}

function daysInMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * 月末の日を超えないように丸める（1月31日の1か月後は2月28日として扱う）。
 */
function addMonths(date, count) {
  const result = new Date(date.getFullYear(), date.getMonth() + count, 1);

  result.setDate(Math.min(date.getDate(), daysInMonth(result)));

  return result;
}

function clampDate(date, min, max) {
  if (date < min) {
    return min;
  }

  if (date > max) {
    return max;
  }

  return date;
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (character) => HTML_ENTITIES[character]);
}

class DeliveryDatePicker extends HTMLElement {
  #abortController;

  /** 表示中の月（その月の1日） */
  #viewDate;

  /** キーボード操作でいま focus 対象になっている日 */
  #activeDate;

  connectedCallback() {
    this.#abortController = new AbortController();

    this.addEventListener('change', this.#onChange, { signal: this.#abortController.signal });

    if (this.#input && this.#trigger && this.#calendar) {
      this.#setupCalendar();
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #onChange = (event) => {
    const field = event.target;

    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) {
      return;
    }

    if (field.type === 'date' && !this.#validateDate(field)) {
      return;
    }

    this.#showError('');
    this.#save();
  };

  #validateDate(field) {
    if (field.value === '') {
      return true;
    }

    const rejection = this.#rejectionFor(field.value);

    if (rejection) {
      this.#reject(field, rejection);
      return false;
    }

    return true;
  }

  /**
   * 選べない日であればその理由を返し、選べる日であれば null を返す。
   * 日付は ISO 形式（YYYY-MM-DD）なので、文字列の比較がそのまま日付の前後の比較になる。
   */
  #rejectionFor(value) {
    const { min, max } = this.#input;

    if ((min !== '' && value < min) || (max !== '' && value > max)) {
      return this.getAttribute('range-message');
    }

    if (this.#blackoutDates.includes(value)) {
      return this.getAttribute('blackout-message');
    }

    return null;
  }

  #reject(field, message) {
    field.value = '';
    this.#updateTriggerLabel();
    this.#showError(message);
    this.#save();
  }

  get #blackoutDates() {
    return (this.getAttribute('blackout-dates') || '').split(/[\s,、]+/).filter(Boolean);
  }

  #showError(message) {
    const errorElement = this.querySelector('[data-delivery-error]');

    if (!errorElement) {
      return;
    }

    errorElement.textContent = message || '';
    errorElement.hidden = !message;
  }

  #save() {
    const attributes = {};

    for (const field of this.querySelectorAll('[name^="attributes["]')) {
      const name = field.getAttribute('name').match(ATTRIBUTE_NAME_PATTERN)?.[1];

      if (name) {
        attributes[name] = field.value;
      }
    }

    fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
      keepalive: true, // フォーム送信と同時に発火しても確実にリクエストが飛ぶようにする
    });
  }

  /**
   * ----------------------------------------------------------------------------------------------
   * カレンダー
   * ----------------------------------------------------------------------------------------------
   */

  get #input() {
    return this.querySelector('[data-delivery-date-input]');
  }

  get #trigger() {
    return this.querySelector('[data-delivery-trigger]');
  }

  get #calendar() {
    return this.querySelector('[data-delivery-calendar]');
  }

  get #isOpen() {
    return !this.#calendar.hidden;
  }

  get #locale() {
    return document.documentElement.lang || undefined;
  }

  get #minDate() {
    return fromIsoDate(this.#input.min);
  }

  get #maxDate() {
    return fromIsoDate(this.#input.max);
  }

  get #selectedDate() {
    return this.#input.value === '' ? null : fromIsoDate(this.#input.value);
  }

  #setupCalendar() {
    const { signal } = this.#abortController;

    this.#input.hidden = true;
    this.#trigger.hidden = false;
    this.#updateTriggerLabel();

    this.#trigger.addEventListener('click', () => this.#toggleCalendar(), { signal });
    this.#calendar.addEventListener('click', this.#onCalendarClick, { signal });
    this.#calendar.addEventListener('keydown', this.#onCalendarKeydown, { signal });

    // 非表示にした input を指しているままだと、ラベルを押しても何も起きないため
    this.querySelector('[data-delivery-label]')?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.#openCalendar();
      },
      { signal }
    );

    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onDocumentKeydown, { signal });
  }

  #toggleCalendar() {
    this.#isOpen ? this.#closeCalendar() : this.#openCalendar();
  }

  #openCalendar() {
    // 未選択なら、最短で選べる日の月から見せる
    this.#activeDate = this.#selectedDate || clampDate(new Date(), this.#minDate, this.#maxDate);
    this.#viewDate = startOfMonth(this.#activeDate);

    this.#render();

    this.#calendar.hidden = false;
    this.#trigger.setAttribute('aria-expanded', 'true');

    this.#focusActiveDay();
  }

  #closeCalendar({ focusTrigger = false } = {}) {
    this.#calendar.hidden = true;
    this.#trigger.setAttribute('aria-expanded', 'false');

    if (focusTrigger) {
      this.#trigger.focus();
    }
  }

  #onDocumentClick = (event) => {
    // 月を切り替えた直後は、押されたボタン自体が再描画で DOM から外れている。
    // contains() では「中の操作」と「外側のクリック」を区別できないため、
    // クリック時点の経路が記録されている composedPath() で判定する。
    if (this.#isOpen && !event.composedPath().includes(this)) {
      this.#closeCalendar();
    }
  };

  #onDocumentKeydown = (event) => {
    if (this.#isOpen && event.key === 'Escape') {
      this.#closeCalendar({ focusTrigger: true });
    }
  };

  #onCalendarClick = (event) => {
    const navigation = event.target.closest('[data-delivery-month-step]');

    if (navigation) {
      this.#goToMonth(Number(navigation.dataset.deliveryMonthStep));
      return;
    }

    if (event.target.closest('[data-delivery-clear]')) {
      this.#selectDate('');
      return;
    }

    const day = event.target.closest('[data-date]');

    if (!day) {
      return;
    }

    // 選べない日は、なぜ選べないのかを伝えたいので押せるままにしてある
    if (day.getAttribute('aria-disabled') === 'true') {
      this.#showError(this.#rejectionFor(day.dataset.date));
      return;
    }

    this.#selectDate(day.dataset.date);
  };

  #onCalendarKeydown = (event) => {
    let target = null;

    if (event.key in KEY_DAY_OFFSETS) {
      target = addDays(this.#activeDate, KEY_DAY_OFFSETS[event.key]);
    } else if (event.key === 'Home') {
      target = addDays(this.#activeDate, -this.#activeDate.getDay());
    } else if (event.key === 'End') {
      target = addDays(this.#activeDate, 6 - this.#activeDate.getDay());
    } else if (event.key === 'PageUp') {
      target = addMonths(this.#activeDate, -1);
    } else if (event.key === 'PageDown') {
      target = addMonths(this.#activeDate, 1);
    } else {
      return;
    }

    event.preventDefault();
    this.#moveTo(target);
  };

  #moveTo(date) {
    this.#activeDate = clampDate(date, this.#minDate, this.#maxDate);

    const month = startOfMonth(this.#activeDate);

    if (month.getTime() !== this.#viewDate.getTime()) {
      this.#viewDate = month;
      this.#render();
    } else {
      this.#updateActiveDay();
    }

    this.#focusActiveDay();
  }

  #goToMonth(step) {
    this.#viewDate = addMonths(this.#viewDate, step);

    // 移動先の月の中に focus 位置を持っていく
    this.#activeDate = clampDate(
      new Date(this.#viewDate.getFullYear(), this.#viewDate.getMonth(), Math.min(this.#activeDate.getDate(), daysInMonth(this.#viewDate))),
      this.#minDate,
      this.#maxDate
    );

    this.#render();
    this.#focusActiveDay();
  }

  #selectDate(value) {
    this.#input.value = value;

    // 既存の保存処理（#onChange）に流す
    this.#input.dispatchEvent(new Event('change', { bubbles: true }));

    this.#updateTriggerLabel();
    this.#closeCalendar({ focusTrigger: true });
  }

  #updateTriggerLabel() {
    const label = this.querySelector('[data-delivery-trigger-label]');

    if (!label) {
      return;
    }

    const value = this.#input.value;

    label.textContent = value === '' ? this.getAttribute('placeholder-label') : this.#formatDate(fromIsoDate(value), 'short');
    this.#trigger.classList.toggle('delivery-calendar__trigger--empty', value === '');
  }

  #formatDate(date, weekday) {
    return new Intl.DateTimeFormat(this.#locale, { year: 'numeric', month: 'long', day: 'numeric', weekday }).format(date);
  }

  #focusActiveDay() {
    this.#calendar.querySelector('[data-date][tabindex="0"]')?.focus();
  }

  #updateActiveDay() {
    const activeIso = toIsoDate(this.#activeDate);

    for (const day of this.#calendar.querySelectorAll('[data-date]')) {
      day.tabIndex = day.dataset.date === activeIso ? 0 : -1;
    }
  }

  #render() {
    const monthLabel = new Intl.DateTimeFormat(this.#locale, { year: 'numeric', month: 'long' }).format(this.#viewDate);

    this.#calendar.innerHTML = `
      <div class="delivery-calendar__header">
        ${this.#renderMonthStep(-1, this.getAttribute('previous-month-label'))}
        <span class="delivery-calendar__month">${escapeHtml(monthLabel)}</span>
        ${this.#renderMonthStep(1, this.getAttribute('next-month-label'))}
      </div>

      <div class="delivery-calendar__weekdays" aria-hidden="true">${this.#renderWeekdays()}</div>

      <div class="delivery-calendar__grid" role="group" aria-label="${escapeHtml(monthLabel)}">${this.#renderDays()}</div>

      <div class="delivery-calendar__footer">
        <button type="button" class="delivery-calendar__clear text-sm" data-delivery-clear>
          ${escapeHtml(this.getAttribute('clear-label'))}
        </button>
      </div>
    `;
  }

  #renderMonthStep(step, label) {
    const target = addMonths(this.#viewDate, step);

    // 選べる日が1日もない月には行かせない
    const disabled = step < 0 ? new Date(target.getFullYear(), target.getMonth(), daysInMonth(target)) < this.#minDate : target > this.#maxDate;

    return `
      <button
        type="button"
        class="delivery-calendar__step"
        data-delivery-month-step="${step}"
        aria-label="${escapeHtml(label)}"
        ${disabled ? 'disabled' : ''}
      >
        <svg aria-hidden="true" focusable="false" fill="none" width="10" viewBox="0 0 10 10">
          <path d="${step < 0 ? 'M7 1 3 5l4 4' : 'm3 9 4-4-4-4'}" stroke="currentColor" stroke-linecap="square"/>
        </svg>
      </button>
    `;
  }

  #renderWeekdays() {
    const formatter = new Intl.DateTimeFormat(this.#locale, { weekday: 'short' });
    const cells = [];

    for (let index = 0; index < 7; index++) {
      // 2023年1月1日は日曜日。曜日名を取り出すためだけに使う
      const date = new Date(2023, 0, 1 + index);

      cells.push(`<span class="delivery-calendar__weekday ${this.#weekendModifier(index, 'weekday')}">${escapeHtml(formatter.format(date))}</span>`);
    }

    return cells.join('');
  }

  #renderDays() {
    const firstDay = startOfMonth(this.#viewDate);
    const totalDays = daysInMonth(this.#viewDate);
    const todayIso = toIsoDate(new Date());
    const selectedIso = this.#input.value;
    const activeIso = toIsoDate(this.#activeDate);

    const cells = [];

    // 1日が何曜日かに合わせて、週の頭から空きマスを置く
    for (let index = 0; index < firstDay.getDay(); index++) {
      cells.push('<span class="delivery-calendar__day delivery-calendar__day--empty"></span>');
    }

    for (let dayOfMonth = 1; dayOfMonth <= totalDays; dayOfMonth++) {
      const date = new Date(this.#viewDate.getFullYear(), this.#viewDate.getMonth(), dayOfMonth);
      const iso = toIsoDate(date);
      const rejection = this.#rejectionFor(iso);

      const classes = [
        'delivery-calendar__day',
        this.#weekendModifier(date.getDay(), 'day'),
        iso === todayIso ? 'delivery-calendar__day--today' : '',
      ];

      cells.push(`
        <button
          type="button"
          class="${classes.filter(Boolean).join(' ')}"
          data-date="${iso}"
          tabindex="${iso === activeIso ? '0' : '-1'}"
          aria-label="${escapeHtml(this.#formatDate(date, 'long'))}"
          aria-pressed="${iso === selectedIso}"
          ${rejection ? 'aria-disabled="true"' : ''}
        >${dayOfMonth}</button>
      `);
    }

    return cells.join('');
  }

  #weekendModifier(dayOfWeek, block) {
    if (dayOfWeek === 0) {
      return `delivery-calendar__${block}--sunday`;
    }

    if (dayOfWeek === 6) {
      return `delivery-calendar__${block}--saturday`;
    }

    return '';
  }
}

if (!window.customElements.get('delivery-date-picker')) {
  window.customElements.define('delivery-date-picker', DeliveryDatePicker);
}
